import { useId, useRef, useState, type ReactNode } from 'react';
import { Info } from 'lucide-react';
import { Popover as PopoverPrimitive } from 'radix-ui';
import type { EquacaoPrecoResponse } from '@triade/contracts';
import { MarginBar } from '@/components/margin-bar';
import { Skeleton } from '@/components/ui/skeleton';
import { PriceEquation } from '@/components/price-equation';
import { formatarDinheiro, formatarFracaoPercentual, formatarMarkup } from '@/lib/format';

export interface DespesaVariavelItem {
  tipo: 'T' | 'C' | 'O';
  descricao: string;
  /** Fracao do preco (string vinda da API). */
  taxa: string;
  /** R$ da linha no preco sugerido (API). */
  valorSugerido?: string;
  /** R$ da linha no preco praticado (API). */
  valorPraticado?: string;
}

/** Qual preco as colunas de R$ mostram: o sugerido ou o praticado (cada painel escolhe o seu). */
export type BaseValoresDespesa = 'sugerido' | 'praticado';

const ROTULO_TIPO: Record<DespesaVariavelItem['tipo'], string> = { T: 'Tributo', C: 'Comissão', O: 'Outra' };

export interface PainelPrecoProps {
  titulo: ReactNode;
  badge?: ReactNode;
  equacao: EquacaoPrecoResponse;
  rotuloMarkup?: string;
  /** Nota sob o markup (ja formatada pelo chamador). */
  markupNota?: string;
  rotuloPreco: string;
  className?: string;
  /** Despesas variaveis da composicao (API). Sem itens, nao ha botao de detalhe. */
  despesasVariaveis?: DespesaVariavelItem[];
  /** Taxa total das despesas variaveis (API). */
  despesasVariaveisTotal?: string;
  /** Bloco extra no rodape do cartao (ex.: margem de contribuicao), ja montado pelo chamador. */
  extra?: ReactNode;
  /** Base dos R$ do detalhe de despesas variaveis (padrao: sugerido). */
  baseValores?: BaseValoresDespesa;
}

/** Equacao do preco + "para onde vai cada real" de uma equacao ja calculada pela API. Presentacional. */
export function PainelPreco({
  titulo,
  badge,
  equacao,
  rotuloMarkup,
  markupNota,
  rotuloPreco,
  className,
  despesasVariaveis,
  despesasVariaveisTotal,
  extra,
  baseValores = 'sugerido',
}: PainelPrecoProps) {
  const s = equacao.segmentos;
  const temDetalhe = (despesasVariaveis?.length ?? 0) > 0;
  return (
    <div className={`flex flex-col rounded-2xl border border-border bg-card ${className ?? ''}`}>
      <PriceEquation
        className="rounded-b-none border-0 pb-[18px]"
        titulo={titulo}
        badge={badge}
        custoUnitario={equacao.custoUnitario}
        markup={equacao.markup ?? ''}
        rotuloMarkup={rotuloMarkup}
        markupNota={markupNota}
        aVista={equacao.aVista}
        custoFinanceiro={equacao.custoFinanceiro}
        custoFinanceiroNota={`${formatarFracaoPercentual(equacao.taxaFinanceira)} a prazo`}
        preco={equacao.preco}
        rotuloPreco={rotuloPreco}
        semIpi={equacao.semIpi}
        ipi={equacao.ipi}
      />
      <MarginBar
        className="px-5 pb-5"
        titulo="Para onde vai cada real do preço (sem IPI)"
        segmentos={[
          { chave: 'custo', rotulo: 'Custo', fracao: s.custo.fracao, valor: s.custo.valor },
          { chave: 'variavel', rotulo: 'Desp. variável', fracao: s.despesaVariavel.fracao, valor: s.despesaVariavel.valor },
          { chave: 'fixa', rotulo: 'Desp. fixa', fracao: s.despesaFixa.fracao, valor: s.despesaFixa.valor },
          { chave: 'lucro', rotulo: 'Lucro', fracao: s.lucro.fracao, valor: s.lucro.valor },
        ]}
        legendaExtra={
          temDetalhe
            ? {
                variavel: (
                  <DetalheDespesasVariaveis
                    despesas={despesasVariaveis!}
                    total={despesasVariaveisTotal}
                    base={baseValores}
                    totalValor={s.despesaVariavel.valor}
                  />
                ),
              }
            : undefined
        }
      />
      {extra}
    </div>
  );
}

/** Botao que abre a lista das despesas variaveis (tipo, descricao, taxa e R$ da base escolhida) e o total, todos vindos da API. */
export function DetalheDespesasVariaveis({
  despesas,
  total,
  base = 'sugerido',
  totalValor,
}: {
  despesas: DespesaVariavelItem[];
  total?: string;
  base?: BaseValoresDespesa;
  /** R$ total das despesas variaveis do proprio painel (API). */
  totalValor?: string;
}) {
  const [aberto, setAberto] = useState(false);
  const idConteudo = useId();
  const ancora = useRef<HTMLButtonElement>(null);
  const valorDaLinha = (d: DespesaVariavelItem) => formatarDinheiro(base === 'praticado' ? d.valorPraticado : d.valorSugerido);

  return (
    <PopoverPrimitive.Root open={aberto} onOpenChange={setAberto}>
      <PopoverPrimitive.Anchor asChild>
        <button
          ref={ancora}
          type="button"
          aria-label="Ver despesas variáveis"
          aria-expanded={aberto}
          aria-controls={idConteudo}
          onClick={() => setAberto(true)}
          onFocus={() => setAberto(true)}
          className="inline-flex size-5 shrink-0 items-center justify-center rounded-full text-ink-muted outline-none hover:text-ink-body focus-visible:shadow-focus"
        >
          <Info aria-hidden="true" className="size-3.5" />
        </button>
      </PopoverPrimitive.Anchor>
      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Content
          id={idConteudo}
          role="dialog"
          aria-label="Despesas variáveis"
          align="end"
          sideOffset={6}
          // Foco permanece no botao (acessivel por teclado); Esc fecha sem reabrir pelo foco.
          onOpenAutoFocus={(e) => e.preventDefault()}
          onCloseAutoFocus={(e) => e.preventDefault()}
          // Clique no proprio botao nao fecha: so Esc ou clique fora do botao.
          onPointerDownOutside={(e) => {
            if (ancora.current?.contains(e.target as Node)) e.preventDefault();
          }}
          className="z-50 w-80 max-w-[calc(100vw-2rem)] rounded-xl border border-border bg-popover p-3 text-small shadow-menu"
        >
          <ul className="flex flex-col gap-2">
            {despesas.map((d, i) => (
              <li key={`${d.tipo}-${i}`} className="grid grid-cols-[auto_minmax(0,1fr)_auto_auto] items-center gap-2">
                <span className="justify-self-start rounded-xs bg-line-soft px-1.5 py-0.5 text-caption">{ROTULO_TIPO[d.tipo]}</span>
                <span className="min-w-0 truncate">{d.descricao}</span>
                <span className="tabular whitespace-nowrap text-right text-ink-secondary">{formatarFracaoPercentual(d.taxa)}</span>
                <span className="tabular whitespace-nowrap text-right">{valorDaLinha(d)}</span>
              </li>
            ))}
          </ul>
          <div className="mt-3 grid grid-cols-[auto_minmax(0,1fr)_auto_auto] items-center gap-2 border-t border-border pt-2 font-semibold">
            <span className="col-span-2">Total</span>
            <span className="tabular whitespace-nowrap text-right">{formatarFracaoPercentual(total)}</span>
            <span className="tabular whitespace-nowrap text-right">{formatarDinheiro(totalValor)}</span>
          </div>
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>
  );
}

/** "+0,2332 sobre o sugerido" a partir do texto da API (so reescreve o sinal). */
export function notaMarkupSobreSugerido(delta: string | null): string | undefined {
  if (delta === null) return undefined;
  const t = formatarMarkup(delta);
  if (t === '—') return undefined;
  return `${t.startsWith('-') ? '' : '+'}${t} sobre o sugerido`;
}

export interface PainelPrecoEsqueletoProps {
  /** Titulo do cartao (mesmo do PainelPreco que vai ocupar o lugar). */
  titulo: ReactNode;
  /** Nome acessivel do cartao (aria-label). */
  nome: string;
  rotuloMarkup?: string;
  rotuloPreco: string;
  /** Texto de apoio no lugar dos valores (ex.: "Informe o preço praticado..."). */
  dica?: string;
  className?: string;
}

const CELULA_ESQUELETO = 'rounded-[10px] bg-surface-muted px-2 py-3 text-center';
const LINHA_ESQUELETO = 'grid grid-cols-[1fr_auto_1fr_auto_1fr] items-center gap-2.5';

/**
 * Esqueleto do PainelPreco: mesmo cartao, titulo, linhas da equacao e barra, com "—" no lugar dos valores. Usado quando a
 * formacao ainda nao tem valor (sem item/tabela/condicao, sem praticado, politica NEGOCIADO). Nao calcula nada.
 */
export function PainelPrecoEsqueleto({ titulo, nome, rotuloMarkup = 'Markup', rotuloPreco, dica, className }: PainelPrecoEsqueletoProps) {
  const celula = (rotulo: string) => (
    <div className={CELULA_ESQUELETO}>
      <div className="text-caption text-ink-muted">{rotulo}</div>
      <div className="tabular mt-0.5 font-semibold text-ink-disabled" aria-hidden="true">
        —
      </div>
    </div>
  );
  const operador = (simbolo: string) => (
    <div className="text-[18px] text-ink-disabled" aria-hidden="true">
      {simbolo}
    </div>
  );
  return (
    <div aria-label={nome} role="region" data-testid="painel-esqueleto" className={`flex min-w-0 flex-col rounded-2xl border border-dashed border-border bg-card ${className ?? ''}`}>
      <div className="flex min-w-0 flex-col gap-[18px] p-5 pb-[18px]">
        <h2 className="min-w-0 break-words text-title font-semibold text-ink-muted">{titulo}</h2>
        <div className={LINHA_ESQUELETO}>
          {celula('Custo unitário')}
          {operador('×')}
          {celula(rotuloMarkup)}
          {operador('=')}
          {celula('À vista')}
        </div>
        <div className={LINHA_ESQUELETO}>
          {celula('À vista')}
          {operador('+')}
          {celula('Custo financeiro')}
          {operador('=')}
          {celula(rotuloPreco)}
        </div>
      </div>
      <div className="flex flex-col gap-3 px-5 pb-5">
        <div className="text-small font-medium text-ink-muted">Para onde vai cada real do preço (sem IPI)</div>
        <Skeleton className="h-3.5 w-full rounded-full" />
      </div>
      {dica && <p className="min-w-0 break-words px-5 pb-5 text-small text-ink-muted">{dica}</p>}
    </div>
  );
}
